/**
 * Types mirroring the backoffice API response schemas in
 * seiki-backend/app/schemas/backoffice/. Kept hand-written rather than
 * generated so the console can be read without the backend open, but the field
 * names match one-for-one — if a name here drifts, the API is the authority.
 */

export type OrgType = 'media_owner' | 'media_agency' | 'brand_advertiser';

/**
 * Raw `users.org_role` values — a position in the customer's organization, and
 * exactly two of them per D11. Seiki staff elevation is NOT an org role: it is
 * a separate boolean (`is_seiki_staff`). Folding the two into one control is
 * what silently erased org positions before AD-09.
 */
export type OrgRole = 'org_admin' | 'org_member';

/** The value of the API's `effective_role` field — `User.role` server-side.
 *
 * Nothing stores it. AD-15 dropped the column that used to
 * (`users.role_type`), because it was a projection of two facts that live
 * elsewhere, written in a third place and synced by hand. The backend now
 * computes it on read (`roles.role_label`): an org admin is `admin`, and
 * everyone else takes the label their workspace membership implies. So it is a
 * *summary* of the org position and the workspace role, not a fourth fact
 * beside them — which is why the console no longer displays it anywhere. It is
 * kept on the row types because the API still returns it; read `org_role` and
 * the workspace memberships instead, and show those.
 *
 * Seiki-staff elevation is not in here: it is a boolean on the user row
 * (`is_seiki_staff`), orthogonal to which role someone holds in their org. It
 * used to be a fourth value, which overwrote the real one on the way out. */
export type EffectiveRole = 'admin' | 'team_member' | 'external';

export type WorkspaceRole = 'workspace_admin' | 'workspace_contributor' | 'workspace_viewer';

export type UserStatus = 'active' | 'pending' | 'deactive';

export interface Paged<T> {
  items: T[];
  total: number;
}

export interface AdminProfile {
  id: number;
  email: string;
  role: string;
  first_name?: string | null;
  last_name?: string | null;
}

/**
 * The bare org row the write endpoints echo back — `OrganizationResponse`
 * server-side. Kept apart from `Organization` because the list endpoint is the
 * only one that computes the counts and the page access below it, and typing a
 * create response as the richer shape would promise fields that aren't there.
 */
export interface OrganizationSummary {
  id: number;
  name: string;
  org_type: OrgType;
  tenant_id: number | null;
  is_active: boolean;
}

/** One row of GET /organizations. */
export interface Organization extends OrganizationSummary {
  workspace_count: number;
  /** What this customer bought. See `OrgFeatures`. */
  features: OrgFeatures;
  /**
   * Which measurement markets this org's workspaces may be pinned to (AD-32).
   *
   * The raw stored value: `null` = unrestricted, a list = exactly those, `[]` =
   * none (which blocks workspace creation entirely). `null` is not the same as
   * a list naming every market today — only `null` keeps following the platform
   * when a new market ships.
   *
   * Not part of `OrgFeatures`, and deliberately so: those decide what this
   * customer's console *shows*, this decides which market their inventory is
   * measured against. It has a hard external bound — the markets the
   * measurement API accepts, served by `countryApi.list()` — so it is not
   * something Seiki can choose to allow.
   */
  allowed_countries: string[] | null;
  /**
   * The market a workspace in this org gets when none is named — its default
   * workspace, and every self-serve signup on its subdomain, which has no way
   * to ask. `null` = unset, and then every workspace must name its own country
   * and signup on this org's address is refused.
   *
   * This is where the tenant's `default_country` went. An org can hold one
   * honestly — it is one commercial entity — where a whitelabel running one
   * workspace per country could not: Orange's tenant-level `CI` silently
   * claimed its Senegal workspace.
   */
  default_country: string | null;
  /** What `allowed_countries` resolves to; every selectable market when null. */
  selectable_countries: string[];
  /** Branding images on this org's whitelabel site (AD-141). See `OrgBranding`. */
  branding: OrgBranding;
  /**
   * The console language a *new* member of this org starts in, as a BCP-47 tag
   * ('en-US' | 'fr-FR'). Never null: every org has one.
   *
   * Prospective only. Changing it does not restate the language of people
   * already in the org — those are individual choices made on the account
   * page. It exists because AD-84 put the language on the user and left
   * accounts provisioned here falling to 'en-US', so a French customer's new
   * admin came up in English and had to fix it by hand.
   */
  default_language: string;
}

/** The console languages an org may default to. Mirrors the backend's
 * app/core/languages.py SUPPORTED_LANGUAGES — both need a locale file in
 * seiki_web/src/pages/locales/ before a new one can be added here. */
export const ORG_LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'en-US', label: 'English (en-US)' },
  { value: 'fr-FR', label: 'Français (fr-FR)' },
];

/** One selectable measurement market, from GET /backoffice/countries. */
export interface CountryOption {
  iso2: string;
  iso3: string;
  name: string;
  /** ISO-4217 currency of this market, e.g. 'XOF' (DL-14). */
  currency: string;
}

/** One currency a workspace may be set to, from GET /backoffice/currencies. */
export interface CurrencyOption {
  code: string;
  name: string;
}

/**
 * The product scope an organization holds — the only place any of it lives.
 *
 * It used to be `tenants.features`, a free-form bag keyed by whitelabel
 * subdomain: it could say nothing about an org on the bare Seiki domain, it put
 * branding and what-they-bought in one object, and changing it took a pull
 * request against a config file.
 *
 * Three shapes, and the difference is load-bearing:
 *
 *   * **block-lists** (`disabled_modules`, `hidden_inventory_fields`) store what
 *     is *hidden*. Always a list; `[]` hides nothing. The console shows them
 *     inverted, because "what can they see" is the question people arrive with.
 *   * **allow-lists** (`campaign_types`, `visible_metrics`, `visible_charts`)
 *     store what is *permitted*. `null` means unrestricted — which is not the
 *     same as a list naming everything today, because only `null` keeps
 *     following the platform when a new metric ships. `[]` means none.
 *   * **`inventory_notice_url`** is `null` when unset, and only `https://` is
 *     accepted.
 *   * **`pricing_mode` / `pricing_defaults`** are the reseller rate card
 *     (AD-134). `null` on either means the platform's budget-driven default,
 *     which is every customer but PowerFull.
 *   * **`individual_billboard_selection`** (AD-158) is a switch. `true` is the
 *     platform default; `false` keeps the org's campaign wizard to whole
 *     networks, whatever the user's workspace role.
 *
 * Every field here is sent on every save, and the backend overwrites the whole
 * set from what it receives. A field this interface omits is therefore not
 * "left alone" — it is cleared. That is why adding a column to the backend
 * means adding it here in the same change.
 *
 * Mirrors `app/core/org_features.py` in seiki-backend, which is the authority.
 * Keys are the platform's own — hyphenated for modules, snake_case elsewhere.
 */
export interface OrgFeatures {
  disabled_modules: string[];
  hidden_inventory_fields: string[];
  campaign_types: string[] | null;
  visible_metrics: string[] | null;
  visible_charts: string[] | null;
  inventory_notice_url: string | null;
  pricing_mode: PricingMode;
  pricing_defaults: PricingDefaults | null;
  individual_billboard_selection: boolean;
}

/** How a customer's scenarios are priced. `null` is the platform default. */
export type PricingMode = 'rate_card' | null;

/**
 * A reseller's rate card. Mirrors `app.core.pricing.PRICING_DEFAULTS`.
 *
 * This console no longer edits it — the customer sets their own pricing on the
 * Proposal tab of a scenario. It stays on `OrgFeatures` because `setFeatures`
 * sends the whole object and the backend overwrites every column from it, so a
 * field this interface drops is a field cleared on every unrelated save. It is
 * loaded into the draft and sent back untouched.
 *
 * One object rather than separate settings because its numbers are meaningless
 * apart: a base CPM without its premiums prices nothing. Omitted keys fall back
 * to the backend's documented defaults, so a partially-filled card is valid.
 *
 * The volume discount's denominator is deliberately absent: it is the org's own
 * active face count, which the platform already holds. Configuring it would be
 * keeping the same fact in two places, and the copy would go stale the day a
 * face was added.
 */
export interface PricingDefaults {
  /** CPM at 100% of the network, full time, before any negotiation.
   *
   * Required: a card without it prices nothing, and the customer then sees no
   * proposal at all rather than an error.
   */
  base_cpm: number | null;
  /** CPM multiplier per share of voice. Each must be >= 1. */
  share_premiums: { full: number; half: number; quarter: number };
  /** Fractions of the discounted price, both in [0, 1). */
  agency_rebate_pct: number;
  tech_fee_pct: number;
}

export interface OrgOption {
  id: number;
  name: string;
  org_type: OrgType;
  tenant_id: number | null;
  tenant_subdomain: string | null;
}

export interface Workspace {
  id: number;
  slug: string;
  name: string;
  /** ISO-3166-1 alpha-2. Mandatory since AD-32, so never null. */
  country: string;
  /**
   * ISO-4217 budget currency override (DL-14). `null` = the workspace follows
   * its market's currency, which is the usual case.
   */
  currency: string | null;
  /** What budgets in this workspace default to: the override, else the market's. */
  effective_currency: string | null;
  /** ISO-4217 codes budgets may use. `null` = every supported currency. */
  allowed_currencies: string[] | null;
  is_active: boolean;
  is_shared_partner_space: boolean;
  org_id: number;
  org_name: string;
  tenant_subdomain: string | null;
  member_count: number;
}

export interface WorkspaceMember {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: WorkspaceRole;
  is_active: boolean;
  status: string | null;
}

export interface UserRow {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  org_role: string | null;
  effective_role: EffectiveRole;
  status: string | null;
  is_active: boolean;
  org_id: number | null;
  org_name: string | null;
  tenant_subdomain: string | null;
  is_seiki_staff: boolean;
  // No per-user capability booleans. AD-09 step 4 retired can_edit_networks and
  // can_select_individual_billboards (browser-only; the workspace role decides
  // both) along with the privileged_emails whitelist. Step 5 turned the two that
  // were enforced — upload and delete inventory — into capability grants scoped
  // to (user, workspace), with an approver and an optional expiry. A boolean on
  // a user row cannot say "in Senegal, until March", so they are read from
  // capabilityApi.grants({ user_id }) rather than carried here.
  workspace_count: number;
  last_active_at: string | null;
  created_at: string | null;
}

export interface UserWorkspaceMembership {
  workspace_id: number;
  workspace_name: string;
  workspace_slug: string;
  country: string | null;
  role: WorkspaceRole;
}

/** One reason a user cannot be hard-deleted, and how many records say so. */
export interface UserDeletionBlocker {
  kind: string;
  label: string;
  count: number;
}

/**
 * One reason a user cannot be moved to another organization.
 *
 * Same shape as `UserDeletionBlocker` and a strict subset of its causes, but a
 * different question. Deletion asks what would be orphaned; a move asks what
 * would *follow the user across the org boundary* — which is the records still
 * keyed to them by the legacy `owner_id` scoping the client API reads
 * inventory and campaigns through. A KPI job they once started is history and
 * blocks only the delete.
 */
export interface UserMoveBlocker {
  kind: string;
  label: string;
  count: number;
}

/** What `userApi.moveToOrg` changed, counted rather than assumed. */
export interface UserMoveResult {
  user_id: number;
  email: string;
  previous_org_id: number | null;
  previous_org_name: string | null;
  org_id: number;
  org_name: string;
  org_role: string | null;
  tenant_id: number | null;
  workspace_ids: number[];
  memberships_removed: number;
  memberships_kept: number;
  capability_grants_revoked: number;
  capability_requests_withdrawn: number;
  invitations_cancelled: number;
  sessions_revoked: number;
  legacy_owner_link_cleared: boolean;
}

export interface UserDetail extends UserRow {
  country_code: string | null;
  phone_number: string | null;
  company_name: string | null;
  user_type: string | null;
  email_verified_at: string | null;
  workspaces: UserWorkspaceMembership[];
  /**
   * Whether DELETE /users/{id} would go through. Answered on the detail read
   * rather than discovered by trying: a user who owns billboards, campaigns or
   * plans cannot be deleted at all, and the operator should see that before
   * reaching for the button. Deactivation is always available instead.
   */
  deletable: boolean;
  deletion_blockers: UserDeletionBlocker[];
  /**
   * Whether PUT /users/{id}/organization would go through. Answered on the
   * same read, off the same counts as `deletable`, so the console can disable
   * the control and say why instead of letting an operator find out by trying.
   */
  movable: boolean;
  move_blockers: UserMoveBlocker[];
}

export interface Tenant {
  id: number;
  subdomain: string;
  name: string;
  is_active: boolean;
  org_id: number | null;
  org_name: string | null;
  workspace_count: number;
}

export interface TenantDetail {
  id: number;
  subdomain: string;
  name: string;
  primary_color: string | null;
  features: Record<string, unknown> | null;
  lingo: Record<string, string> | null;
  timezone: string | null;
  is_active: boolean;
  org_id: number | null;
  org_name: string | null;
  is_git_managed: boolean;
  config_path: string;
}

export interface TenantConfigDraft {
  config_path: string;
  file_contents: string;
  is_new_tenant: boolean;
  normalized: Record<string, unknown>;
}

/**
 * An org's branding images, shown on its whitelabel site (AD-141).
 *
 * They lived in the tenant's git-owned config file, so a logo change took a pull
 * request and a release. `null` means Seiki's own image. https only.
 */
export interface OrgBranding {
  logo_url: string | null;
  favicon_url: string | null;
  /** Symbol-only short logo for the collapsed sidebar; falls back to the favicon. */
  logo_mark_url: string | null;
  /** Render the logo as a flat white silhouette in the report PDF's dark header. */
  pdf_logo_white_on_dark: boolean;
  /**
   * Banner at the top of every email (about 1200×200). The "powered by" footer
   * is always Seiki's logo and is not branding.
   */
  email_header_url: string | null;
}

/** Which branding image an upload is; mirrors the backend's OrgBrandingAssetKind. */
export type OrgBrandingAssetKind = 'logo' | 'favicon' | 'logo-mark' | 'email-header';

export interface OrgBrandingAssetUpload {
  url: string;
  object_key: string;
}

/** Human labels. Kept next to the types so a new role value fails loudly here. */
export const ORG_ROLE_LABEL: Record<OrgRole, string> = {
  org_admin: 'Org admin',
  org_member: 'Org member',
};

export const WORKSPACE_ROLE_LABEL: Record<WorkspaceRole, string> = {
  workspace_admin: 'Admin',
  workspace_contributor: 'Contributor',
  workspace_viewer: 'Viewer',
};

export const ORG_TYPE_LABEL: Record<OrgType, string> = {
  media_owner: 'Media owner',
  media_agency: 'Media agency',
  brand_advertiser: 'Brand advertiser',
};


/**
 * Capability requests and grants (AD-09 step 5).
 *
 * A grant is scoped to one (user, workspace, capability) and may expire — which
 * is what the retired per-user boolean flags could not express. A request is the
 * record of the asking; a grant is the answer.
 */
export type CapabilityRequestStatus =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'withdrawn';

export interface CapabilityRequest {
  id: number;
  user_id: number;
  user_email: string;
  user_name: string;
  workspace_id: number;
  workspace_slug: string;
  workspace_name: string;
  org_name: string;
  capability: string;
  justification: string | null;
  status: CapabilityRequestStatus;
  decision_note: string | null;
  decided_at: string | null;
  decided_by_admin_id: number | null;
  created_at: string | null;
}

export interface CapabilityGrant {
  id: number;
  user_id: number;
  user_email: string;
  workspace_id: number;
  workspace_slug: string;
  org_name: string;
  capability: string;
  reason: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  granted_by_admin_id: number | null;
  request_id: number | null;
  created_at: string | null;
  /** Accounts for expiry as well as revocation — a live-looking row can be expired. */
  is_live: boolean;
}

/** Human labels. The backend is the authority on which values exist. */
export const CAPABILITY_LABEL: Record<string, string> = {
  upload_inventory: 'Upload inventory',
  soft_delete_inventory: 'Delete inventory',
  edit_networks: 'Edit networks',
  select_individual_billboards: 'Select individual billboards',
  view_gross_contacts: 'View gross contacts',
};

export function capabilityLabel(value: string): string {
  return CAPABILITY_LABEL[value] ?? value;
}
